import {
  Alert,
  AlertIcon,
  Button,
  Flex,
  FormControl,
  FormLabel,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  useDisclosure,
} from '@chakra-ui/react';
import React, { useEffect } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { z } from 'zod';

import { useUpdateImplementationNameMutation } from './[implementationId]/implementationDetails.gql.generated';
import { TextInput } from './common/Inputs';

export const FORM_ID = 'UpdateImplementationNameForm';

const FormValuesStruct = z.object({
  implementationName: z
    .string({ message: 'Must provide a valid name' })
    .trim()
    .min(1, { message: 'Name cannot be empty' }),
});

type FormValues = z.infer<typeof FormValuesStruct>;

type UpdateImplementationNameFormProps = {
  externalErrors: (Error | undefined)[];
  currentName: string;
  onSubmit: (data: FormValues) => void;
};

const UpdateImplementationNameForm = ({
  externalErrors,
  currentName,
  onSubmit,
}: UpdateImplementationNameFormProps) => {
  const methods = useForm<FormValues>({
    defaultValues: {
      implementationName: currentName,
    },
    mode: 'onTouched',
  });

  const { reset, register, handleSubmit, formState } = methods;

  useEffect(() => {
    reset({ implementationName: currentName });
  }, [currentName, reset]);

  useEffect(() => {
    register('implementationName', {
      validate: (value) => {
        const result = FormValuesStruct.shape.implementationName.safeParse(
          value,
        );
        if (result.success) {
          return true;
        }

        return z.prettifyError(result.error);
      },
    });
  }, [register]);

  return (
    <FormProvider {...methods}>
      <form id={FORM_ID} onSubmit={handleSubmit(onSubmit)}>
        <Flex direction="column" gap="1rem">
          {externalErrors.map((error) => {
            if (!error) return null;
            return (
              <Alert key={error.message} status="error">
                <AlertIcon /> {error.message}
              </Alert>
            );
          })}

          <FormControl isReadOnly>
            <FormLabel>Current Implementation Name</FormLabel>
            <Input
              name="currentName"
              value={currentName}
              type="text"
              isDisabled
            />
          </FormControl>

          <TextInput<FormValues>
            name="implementationName"
            label="New Implementation Name"
            placeholder="Enter implementation name"
            required
            defaultValue={currentName}
          />
          {formState.errors.implementationName && (
            <FormControl isInvalid={!!formState.errors.implementationName} />
          )}
        </Flex>
      </form>
    </FormProvider>
  );
};

type UpdateImplementationNameModalProps = {
  modalControls: ReturnType<typeof useDisclosure>;
  implementationId: string;
  currentName: string;
};

export const UpdateImplementationNameModal = ({
  modalControls,
  implementationId,
  currentName,
}: UpdateImplementationNameModalProps) => {
  const [
    updateImplementationName,
    { error: updateError, loading },
  ] = useUpdateImplementationNameMutation();

  const handleFormSubmit = async (formData: FormValues) => {
    const newName = formData.implementationName.trim();

    if (currentName === newName) {
      modalControls.onClose();
      return;
    }

    try {
      await updateImplementationName({
        variables: {
          implementationId,
          name: newName,
        },
      });
      modalControls.onClose();
    } catch (error) {
      // Do nothing as shown in the UpdateFacilityNameModal as well
    }
  };

  return (
    <Modal isOpen={modalControls.isOpen} onClose={modalControls.onClose}>
      <ModalOverlay />
      <ModalContent padding="1rem" minWidth={['100%', '90%', '600px']}>
        <ModalHeader>Edit Implementation Name</ModalHeader>
        <ModalCloseButton />
        <ModalBody minHeight="12rem">
          <UpdateImplementationNameForm
            currentName={currentName}
            externalErrors={updateError ? [updateError] : []}
            onSubmit={handleFormSubmit}
          />
        </ModalBody>
        <ModalFooter gap="2rem" marginBottom="0.5rem">
          <Button
            variant="primary-muted"
            colorScheme="v2-aurora"
            onClick={modalControls.onClose}
            isDisabled={loading}
          >
            Cancel
          </Button>
          <Button
            form={FORM_ID}
            type="submit"
            variant="primary"
            colorScheme="v2-aurora"
            isLoading={loading}
          >
            Save Changes
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
